import React from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Text } from '@/components/ui/therapist/Text';
import { CustomCheckBox } from '@/components/SharedComponents/CustomCheckBox/';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import colors from '@/config/colors';
import { View } from 'react-native';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';
import {
  confirmPasswordValidate,
  passwordValidate,
} from '@/utils/passwordUtils';
import { containsNonAsciiCharacters } from '@/utils/containsNonAsciiCharacters';
import { isValidEmail } from '@/utils/stringValidator';
import { IS_ANDROID } from '@/utils/platform';
import { Links } from '@/config/links';
import { openLink } from '@/utils/linkHelpers';
import { TextLink } from '@/components/TextLink';
import { styles } from './styles';
import { SignUpFormData } from './SignUpFormData';

interface PatientSignUpFormProps {
  control: Control<SignUpFormData>;
  handleSubmit: () => void;
  isDataProcessingConsentConfirmed: boolean;
  setDataProcessingConsentConfirmation: (value: boolean) => void;
  isDataUsageConsentConfirmed: boolean;
  setDataUsageConsentConfirmation: (value: boolean) => void;
  isNewsletterConfirmed: boolean;
  setNewsletterConfirmation: (value: boolean) => void;
  showNewsletter: boolean;
  isSubmitted: boolean;
  filledPassword: string;
  errors: FieldErrors<SignUpFormData>;
}

export const PatientSignUpForm: React.FC<PatientSignUpFormProps> = ({
  control,
  handleSubmit,
  isDataProcessingConsentConfirmed,
  setDataProcessingConsentConfirmation,
  isDataUsageConsentConfirmed,
  setDataUsageConsentConfirmation,
  isNewsletterConfirmed,
  setNewsletterConfirmation,
  showNewsletter,
  isSubmitted,
  filledPassword,
  errors,
}) => {
  const titleText =
    'Registrieren Sie sich hier für neolexon Aphasie als Patient:in';

  const dataProcessingConsentText = (
    <Text>
      Ich willige ein, dass meine personenbezogenen Daten, darunter auch
      Gesundheitsdaten, durch neolexon verarbeitet werden, um eine
      Sprachtherapie durchzuführen. Weitere Informationen zu meinen Rechten, der
      Speicherung und Verarbeitung der Daten finde ich in der
      Datenschutzerklärung. Ich bestätige diese gelesen zu haben. Ich kann meine
      Einwilligung jederzeit widerrufen.{' '}
      <TextLink
        href={Links.datenschutz.href}
        testID="registration-data-link"
        style={styles.openLink}
      >
        Datenschutzerklärung öffnen
      </TextLink>
    </Text>
  );

  const dataUsageConsentText = (
    <Text>
      Ich willige ein, dass meine Nutzungsdaten pseudonymisiert durch neolexon
      verarbeitet werden, um die dauerhafte, technische Funktionsfähigkeit und
      Nutzerfreundlichkeit der neolexon Apps zu gewährleisten sowie diese stetig
      weiterzuentwickeln. Ich kann meine Einwilligung jederzeit widerrufen.
      Diese Einwilligung ist
      <Text style={styles.boldMessage}> optional</Text>.
    </Text>
  );

  return (
    <KeyboardAwareScrollView enableOnAndroid>
      <View style={styles.container}>
        <Text style={styles.title}>{titleText}</Text>
        <Text style={[styles.subtitle]}>
          Und testen Sie 14 Tage kostenlos. Danach entstehen keine automatischen
          Kosten.
        </Text>
        <Text style={[styles.subtitle, { color: colors.red }]}>
          Wichtig: Sie benötigen eine:n Therapeut:in, um unsere App nutzen zu
          können.
        </Text>
        <View style={styles.formWrapper}>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="firstName"
              testID="firstName-register-input"
              label="Vorname"
              rules={{ required: 'Vorname wird benötigt.' }}
              autoCapitalize="words"
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              testID="lastName-register-input"
              name="lastName"
              label="Nachname"
              rules={{ required: 'Nachname wird benötigt.' }}
              autoCapitalize="words"
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="email"
              testID="email-register-input"
              label="E-Mail-Adresse"
              keyboardType="email-address"
              rules={{
                required: 'E-Mail-Adresse wird benötigt.',
                validate: (value: string) => {
                  if (containsNonAsciiCharacters(value)) {
                    return 'E-Mail-Adresse enthält ungültige Zeichen';
                  }

                  if (!isValidEmail(value)) {
                    return 'E-Mail-Adresse ist ungültig';
                  }

                  return true;
                },
              }}
            />
          </View>
          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="password"
              testID="password-register-input"
              label="Passwort (mindestens 8 Zeichen)"
              secureTextEntry={true}
              rules={{
                required: 'Passwort wird benötigt.',
                minLength: {
                  value: 8,
                  message:
                    'Passwort muss mindestens 8 Zeichen lang sein und darf Buchstaben, Zahlen, Sonderzeichen und Leerzeichen enthalten.',
                },
                validate: (value: string) => passwordValidate(value),
              }}
            />
          </View>

          <View style={styles.formField}>
            <SharedFormField
              containerStyle={styles.inputText}
              inputContainerStyle={styles.inputContainerStyle}
              errorMessageStyle={styles.errorMessage}
              control={control}
              name="confirmPassword"
              testID="confirmPassword-register-input"
              label="Wiederholung Passwort"
              secureTextEntry={true}
              rules={{
                required: 'Passwortwiederholung wird benötigt.',
                validate: (value: string) =>
                  confirmPasswordValidate(value, filledPassword),
              }}
            />
          </View>
        </View>
        <View style={styles.checkboxWrapper}>
          <View style={styles.checkbox}>
            <CustomCheckBox
              onChecked={() =>
                setDataProcessingConsentConfirmation(
                  !isDataProcessingConsentConfirmed,
                )
              }
              checked={isDataProcessingConsentConfirmed}
              isLabelComponent={IS_ANDROID}
              label={
                <PressableNeolexon
                  testID="data-checkbox"
                  onPress={() =>
                    setDataProcessingConsentConfirmation(
                      !isDataProcessingConsentConfirmed,
                    )
                  }
                >
                  <Text style={styles.checkBoxText}>
                    {dataProcessingConsentText}{' '}
                    {!isDataProcessingConsentConfirmed ? (
                      <Text
                        style={[
                          isSubmitted
                            ? styles.mandatoryErrorCheck
                            : styles.mandatoryTextCheck,
                        ]}
                      >
                        (Pflichtfeld)
                      </Text>
                    ) : null}
                  </Text>
                </PressableNeolexon>
              }
            />
          </View>

          <View style={styles.checkbox}>
            <CustomCheckBox
              checked={isDataUsageConsentConfirmed}
              onChecked={() => {
                setDataUsageConsentConfirmation(!isDataUsageConsentConfirmed);
              }}
              isLabelComponent={IS_ANDROID}
              label={
                <PressableNeolexon
                  onPress={() =>
                    setDataUsageConsentConfirmation(
                      !isDataUsageConsentConfirmed,
                    )
                  }
                >
                  <Text style={styles.checkBoxText}>
                    {dataUsageConsentText}
                  </Text>
                </PressableNeolexon>
              }
            />
          </View>

          <View style={styles.checkbox}>
            {showNewsletter ? (
              <CustomCheckBox
                checked={isNewsletterConfirmed}
                onChecked={() =>
                  setNewsletterConfirmation(!isNewsletterConfirmed)
                }
                isLabelComponent={IS_ANDROID}
                label={
                  <View>
                    <PressableNeolexon
                      onPress={() =>
                        setNewsletterConfirmation(!isNewsletterConfirmed)
                      }
                    >
                      <Text style={styles.checkBoxText}>
                        Ich möchte den Newsletter erhalten.
                      </Text>
                    </PressableNeolexon>
                  </View>
                }
              />
            ) : null}
          </View>
        </View>

        <View style={styles.subtitle2Container}>
          <Text style={styles.subtitle2}>
            Mit Ihrer Registrierung stimmen Sie den{'  '}
            <Text
              testID="registration-agb-link"
              onPress={() => openLink(Links.agb.href)}
              style={[styles.openLink, styles.font20]}
            >
              {Links.agb.label}
            </Text>
            {'  '}
            zu.{'\n'}
          </Text>
        </View>

        <PressableNeolexon
          onPress={handleSubmit}
          style={styles.registrationButton}
          testID="register"
        >
          <Text style={styles.registration}> Registrieren </Text>
        </PressableNeolexon>
      </View>
    </KeyboardAwareScrollView>
  );
};
